/**
 * CRO Collection Page — Interactivity
 * MBur Candle Co.
 *
 * Modules:
 * 1. Sorting
 * 2. Client-side Filtering
 * 3. Sticky Filter Bar (mobile)
 * 4. Load More (AJAX pagination)
 * 5. Quick Add to Cart
 * 6. Variant Dropdown Selection
 * 7. Mobile Sticky ATC Bar
 * 8. FAQ Accordion animation
 * 9. Scroll Reveal
 * 10. Slot Modal + Notes Modal
 */

(function () {
  'use strict';

  // Debounce utility
  function debounce(fn, delay) {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, arguments), delay);
    };
  }

  /* Scroll lock, shared by the modals and the mobile filter drawer.
     `body { overflow: hidden }` does NOT stop touch scrolling in iOS Safari —
     the page kept scrolling behind the reviews modal. Pinning the body with
     position:fixed does; we stash the offset and restore it on unlock. */
  var cpSavedScrollY = 0;
  function cpLockScroll() {
    cpSavedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = (-cpSavedScrollY) + 'px';
    document.body.classList.add('no-scroll', 'cp-scroll-locked');
  }
  function cpUnlockScroll() {
    document.body.classList.remove('no-scroll', 'cp-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, cpSavedScrollY);
  }

  /* Set once the reviews-modal block below defines it. That block is guarded
     by `if (modal && modalBody)`, so the function is not reliably reachable
     from out here (block-scoped declaration) — this gives Load More a stable
     reference without moving the function. */
  var cpReconcile = null;

  /* ============================================
     1. SORTING
     ============================================ */
  const sortSelect = document.querySelector('.js-sort-select');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  /* ============================================
     2. CLIENT-SIDE FILTERING
     ============================================ */
  const filterState = {
    scent: [],
    size: [],
    price: [],
  };

  const productGrid = document.querySelector('.js-product-grid');
  const emptyState = document.querySelector('.js-empty-state');
  const filterCountBadge = document.querySelector('.js-filter-count');
  const clearBtns = document.querySelectorAll('.js-clear-filters');
  const activeFiltersContainer = document.querySelector('.js-active-filters');

  // Filter group toggle
  document.querySelectorAll('.js-filter-group-toggle').forEach((btn) => {
    btn.addEventListener('click', function () {
      const isOpen = this.getAttribute('aria-expanded') === 'true';
      // Close all other dropdowns
      document.querySelectorAll('.js-filter-group-toggle').forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
        b.closest('.cp-filter-group')
          .querySelector('.cp-filter-group__dropdown')
          .classList.remove('is-open');
      });
      if (!isOpen) {
        this.setAttribute('aria-expanded', 'true');
        this.closest('.cp-filter-group')
          .querySelector('.cp-filter-group__dropdown')
          .classList.add('is-open');
      }
    });
  });

  // Close dropdowns when clicking outside
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('.cp-filter-group')) {
      document.querySelectorAll('.cp-filter-group__dropdown.is-open').forEach((d) => {
        d.classList.remove('is-open');
        d.closest('.cp-filter-group')
          .querySelector('.js-filter-group-toggle')
          .setAttribute('aria-expanded', 'false');
      });
    }
  });

  // Filter checkbox change (size & price dropdowns)
  document.querySelectorAll('[data-filter-type]').forEach((checkbox) => {
    checkbox.addEventListener('change', function () {
      const type = this.dataset.filterType;
      const value = this.value;
      if (this.checked) {
        filterState[type].push(value);
      } else {
        filterState[type] = filterState[type].filter((v) => v !== value);
      }
      applyFilters();
    });
  });

  // Scent pill click handler
  document.querySelectorAll('.js-scent-pill').forEach(function (pill) {
    pill.addEventListener('click', function () {
      var scent = this.dataset.scent;

      if (scent === 'all') {
        filterState.scent = [];
        document.querySelectorAll('.js-scent-pill').forEach(function (p) {
          p.setAttribute('aria-pressed', p.dataset.scent === 'all' ? 'true' : 'false');
        });
      } else {
        var idx = filterState.scent.indexOf(scent);
        if (idx > -1) {
          filterState.scent.splice(idx, 1);
          this.setAttribute('aria-pressed', 'false');
        } else {
          filterState.scent.push(scent);
          this.setAttribute('aria-pressed', 'true');
        }
        var allPill = document.querySelector('.js-scent-pill[data-scent="all"]');
        if (allPill) {
          allPill.setAttribute('aria-pressed', filterState.scent.length === 0 ? 'true' : 'false');
        }
      }

      // Sync any remaining scent checkboxes
      document.querySelectorAll('[data-filter-type="scent"]').forEach(function (cb) {
        cb.checked = filterState.scent.includes(cb.value);
      });

      updateScentNotice();
      applyFilters();
    });
  });

  // Mobile filter drawer toggle
  const filterToggle = document.querySelector('.js-filter-toggle');
  const filterDrawer = document.getElementById('cp-filter-drawer');
  if (filterToggle && filterDrawer) {
    filterToggle.addEventListener('click', function () {
      const isOpen = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', !isOpen);
      filterDrawer.classList.toggle('is-open');
      if (isOpen) { cpUnlockScroll(); } else { cpLockScroll(); }
    });
  }

  // Clear filters
  clearBtns.forEach((btn) => {
    btn.addEventListener('click', function () {
      filterState.scent = [];
      filterState.size = [];
      filterState.price = [];
      document.querySelectorAll('[data-filter-type]').forEach((cb) => {
        cb.checked = false;
      });
      // Reset scent pills
      document.querySelectorAll('.js-scent-pill').forEach(function (p) {
        p.setAttribute('aria-pressed', p.dataset.scent === 'all' ? 'true' : 'false');
      });
      applyFilters();
      // Close mobile drawer
      if (filterDrawer) {
        filterDrawer.classList.remove('is-open');
        cpUnlockScroll();
        if (filterToggle) filterToggle.setAttribute('aria-expanded', 'false');
      }
    });
  });

  // The "any candle can be made unscented" note. Every candle carries the
  // scent-unscented tag on purpose (any of them can be made unscented to
  // order), so the Unscented pill returning all twelve looks like a filter
  // bug without this explanation. Called from applyFilters() rather than from
  // the pill handler alone, because scent state also changes via the sidebar
  // checkboxes, the active-filter chips and Clear all — all of which used to
  // leave the note stale.
  function updateScentNotice() {
    var notice = document.querySelector('.js-scent-notice');
    if (!notice) return;
    notice.style.display = filterState.scent.indexOf('unscented') > -1 ? 'flex' : 'none';
  }

  function applyFilters() {
    updateScentNotice();
    if (!productGrid) return;

    const cards = productGrid.querySelectorAll('.js-product-card');
    const proofInserts = productGrid.querySelectorAll('.cp-proof');
    let visibleCount = 0;
    const totalFilters =
      filterState.scent.length + filterState.size.length + filterState.price.length;

    cards.forEach((card) => {
      const tags = (card.dataset.tags || '').toLowerCase();
      const price = parseInt(card.dataset.price, 10) / 100;
      let visible = true;

      // Scent filter
      if (filterState.scent.length > 0) {
        const matchesScent = filterState.scent.some(
          (s) => tags.includes('scent-' + s) || tags.includes(s)
        );
        if (!matchesScent) visible = false;
      }

      // Size filter
      if (filterState.size.length > 0) {
        const matchesSize = filterState.size.some(
          (s) => tags.includes('size-' + s) || tags.includes(s)
        );
        if (!matchesSize) visible = false;
      }

      // Price filter
      if (filterState.price.length > 0) {
        const matchesPrice = filterState.price.some((range) => {
          const [min, max] = range.split('-').map(Number);
          return price >= min && price <= max;
        });
        if (!matchesPrice) visible = false;
      }

      if (visible) {
        card.classList.remove('is-hidden');
        visibleCount++;
      } else {
        card.classList.add('is-hidden');
      }
    });

    // Show/hide social proof inserts based on visible products
    proofInserts.forEach((proof) => {
      proof.style.display = totalFilters > 0 ? 'none' : '';
    });

    // Empty state
    if (emptyState) {
      emptyState.style.display = visibleCount === 0 && totalFilters > 0 ? 'block' : 'none';
    }

    // Update filter count badge
    if (filterCountBadge) {
      filterCountBadge.textContent = totalFilters;
      filterCountBadge.style.display = totalFilters > 0 ? 'inline-flex' : 'none';
    }

    // Show/hide clear button
    document.querySelectorAll('.cp-filters__clear').forEach((btn) => {
      btn.style.display = totalFilters > 0 ? 'inline-block' : 'none';
    });

    // Render active filter pills
    renderActiveFilters();
  }

  function renderActiveFilters() {
    if (!activeFiltersContainer) return;
    activeFiltersContainer.innerHTML = '';

    const labels = {
      scent: { floral: 'Floral', citrus: 'Citrus', woody: 'Woody', fresh: 'Fresh', warm: 'Warm & Spicy', sweet: 'Sweet', unscented: 'Unscented' },
      size: { small: 'Small', medium: 'Medium', large: 'Large' },
      price: { '0-25': 'Under $25', '25-50': '$25–$50', '50-75': '$50–$75', '75-999': '$75+' },
    };

    Object.keys(filterState).forEach((type) => {
      filterState[type].forEach((value) => {
        const pill = document.createElement('button');
        pill.className = 'cp-filter-pill';
        pill.innerHTML =
          (labels[type]?.[value] || value) +
          ' <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>';
        pill.addEventListener('click', function () {
          filterState[type] = filterState[type].filter((v) => v !== value);
          const checkbox = document.querySelector(
            `[data-filter-type="${type}"][value="${value}"]`
          );
          if (checkbox) checkbox.checked = false;
          // Sync scent pill buttons
          if (type === 'scent') {
            var scentPill = document.querySelector('.js-scent-pill[data-scent="' + value + '"]');
            if (scentPill) scentPill.setAttribute('aria-pressed', 'false');
            var allPill = document.querySelector('.js-scent-pill[data-scent="all"]');
            if (allPill) allPill.setAttribute('aria-pressed', filterState.scent.length === 0 ? 'true' : 'false');
          }
          applyFilters();
        });
        activeFiltersContainer.appendChild(pill);
      });
    });
  }

  /* ============================================
     3. STICKY FILTER BAR (mobile)
     ============================================ */
  const filtersBar = document.getElementById('cp-filters');
  if (filtersBar) {
    // The stylesheet expects a .cp-filters-spacer sibling to hold the bar's
    // space while it is position:fixed, but that element was never added to the
    // markup. Without it, sticking yanked the bar out of flow and every element
    // below jumped up by its height — and because the scroll threshold was
    // measured from the bar itself, the jump could push scrollY back under the
    // threshold and un-stick it, producing the scroll judder. Create the spacer
    // here and measure the threshold from it (it never leaves the flow).
    let spacer = filtersBar.nextElementSibling;
    if (!spacer || !spacer.classList.contains('cp-filters-spacer')) {
      spacer = document.createElement('div');
      spacer.className = 'cp-filters-spacer';
      filtersBar.parentNode.insertBefore(spacer, filtersBar.nextSibling);
    }

    let stuck = false;

    // On mobile the announcement bar and header are BOTH position:fixed at the
    // top with a far higher z-index (10001/10002) than this bar (100). Pinning
    // the filter bar to top:0 slid it underneath them, so it looked like the
    // bar vanished mid-scroll. Offset it below whatever is actually fixed up
    // there, measured live so it survives the bar being shown/hidden.
    function fixedHeaderOffset() {
      let offset = 0;
      ['.announcement-bar', '.site-header-logo-bar'].forEach(function (sel) {
        const el = document.querySelector(sel);
        if (!el) return;
        const cs = window.getComputedStyle(el);
        if (cs.position === 'fixed' && cs.display !== 'none' && cs.visibility !== 'hidden') {
          offset += el.offsetHeight;
        }
      });
      return offset;
    }

    // Where the bar sits in the document, measured from whichever element is
    // currently holding that spot: the bar itself when in flow, the spacer once
    // the bar goes fixed. Both report the same document offset, so the value is
    // stable across the transition and cannot oscillate.
    function barDocTop() {
      const el = stuck ? spacer : filtersBar;
      return el.getBoundingClientRect().top + window.scrollY;
    }

    function unstick() {
      filtersBar.classList.remove('is-sticky');
      filtersBar.style.top = '';
      spacer.style.height = '';
      stuck = false;
    }

    function stickyCheck() {
      if (window.innerWidth >= 768) {
        if (stuck) unstick();
        return;
      }

      const offset = fixedHeaderOffset();

      // Hand over at the exact scroll position where the bar's in-flow top
      // reaches the underside of the fixed header — that is where it will be
      // pinned, so the swap is invisible. Using the bar's own top (offset 0)
      // meant it scrolled fully behind the header and then teleported back
      // down to `offset`, a ~147px jump right as the hero cleared the screen.
      const threshold = barDocTop() - offset;

      if (!stuck && window.scrollY > threshold) {
        spacer.style.height = filtersBar.offsetHeight + 'px'; // reserve first
        filtersBar.style.top = offset + 'px';
        filtersBar.classList.add('is-sticky');
        stuck = true;
      } else if (stuck && window.scrollY <= threshold) {
        unstick();
      } else if (stuck) {
        // the fixed header can change height (announcement bar, menu state)
        filtersBar.style.top = offset + 'px';
      }
    }

    // rAF-throttled instead of debounced: a 10ms debounce only fired after
    // scrolling paused, so the bar stuck late and visibly lagged the scroll.
    let ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        stickyCheck();
        ticking = false;
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      unstick();
      stickyCheck();
    });

    stickyCheck();
  }

  /* ============================================
     4. LOAD MORE (AJAX pagination)
     ============================================ */
  const loadMoreBtn = document.querySelector('.js-load-more');
  if (loadMoreBtn && productGrid) {
    loadMoreBtn.addEventListener('click', async function () {
      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl) return;

      const btnText = this.querySelector('.cp-load-more__text');
      const spinner = this.querySelector('.cp-load-more__spinner');
      btnText.style.display = 'none';
      spinner.style.display = 'inline-block';
      this.disabled = true;

      try {
        const response = await fetch(nextUrl);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newCards = doc.querySelectorAll('.js-product-grid > .js-product-card');
        newCards.forEach((card) => {
          // Lazy load all newly loaded images
          card.querySelectorAll('img').forEach((img) => {
            img.loading = 'lazy';
          });
          productGrid.appendChild(card);
        });

        // Check for next page
        const newLoadMore = doc.querySelector('.js-load-more');
        if (newLoadMore) {
          this.dataset.nextUrl = newLoadMore.dataset.nextUrl;
          btnText.style.display = 'inline';
          spinner.style.display = 'none';
          this.disabled = false;
        } else {
          this.closest('.cp-load-more').remove();
        }

        // Re-apply filters if active
        const totalFilters =
          filterState.scent.length + filterState.size.length + filterState.price.length;
        if (totalFilters > 0) {
          applyFilters();
        }

        // Initialize new card interactions
        initCardInteractions();

        /* Newly appended cards carry Shopify's metafield counts, which lag the
           review feed — and any product with no rating metafield arrives with
           its star row hidden. Reconcile again so page 2+ matches page 1; the
           feed response is already cached, so this costs no extra request. */
        if (cpReconcile) cpReconcile();
      } catch (error) {
        console.error('Load more error:', error);
        btnText.textContent = 'Error — try again';
        btnText.style.display = 'inline';
        spinner.style.display = 'none';
        this.disabled = false;
      }
    });
  }

  /* ============================================
     5. QUICK ADD TO CART (AJAX)
     ============================================ */
  function addToCart(variantId, button) {
    if (!variantId) return;

    const originalText = button.textContent;
    button.classList.add('is-adding');
    button.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: 1,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => {
        button.classList.remove('is-adding');
        button.classList.add('is-added');
        button.textContent = 'Added!';

        // Update cart count in header
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            const countEls = document.querySelectorAll('.js-cart-count, .site-header__cart-count');
            countEls.forEach((el) => {
              el.textContent = cart.item_count;
            });
            // Open cart drawer if available
            if (typeof window.openCartDrawer === 'function') {
              window.openCartDrawer();
            }
          });

        setTimeout(() => {
          button.classList.remove('is-added');
          button.textContent = originalText;
        }, 2000);
      })
      .catch((err) => {
        console.error('ATC error:', err);
        button.classList.remove('is-adding');
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      });
  }

  // Intercept card form submissions for AJAX add
  document.addEventListener('submit', function (e) {
    if (!e.target || !e.target.closest) return;
    const form = e.target.closest('.cp-card__form');
    if (form) {
      e.preventDefault();
      const variantInput = form.querySelector('.js-card-variant-input');
      const button = form.querySelector('.js-add-to-cart');
      if (variantInput && button) {
        addToCart(variantInput.value, button);
        // The corner quick-add pill submits this same form (via form="…"), so
        // mirror the state onto it — otherwise a tap on the pill gives the
        // shopper no feedback at all, since addToCart only knows the main
        // button. Purely cosmetic; the add itself is handled above.
        const card = form.closest('.cp-card');
        const pill = card && card.querySelector('.js-card-quick-add');
        if (pill) {
          pill.classList.add('is-adding');
          setTimeout(function () {
            pill.classList.remove('is-adding');
            pill.classList.add('is-added');
            setTimeout(function () { pill.classList.remove('is-added'); }, 1400);
          }, 400);
        }
      }
    }
  });

  /* ============================================
     6. VARIANT DROPDOWN SELECTION
     ============================================ */
  function initCardInteractions() {
    document.querySelectorAll('.cp-card').forEach((card) => {
      const select = card.querySelector('.js-variant-select');
      if (!select || select.dataset.cpInit) return;
      select.dataset.cpInit = 'true';

      select.addEventListener('change', function () {
        const option = this.options[this.selectedIndex];
        if (!option) return;

        // Update variant ID in form
        const variantInput = card.querySelector('.js-card-variant-input');
        if (variantInput) variantInput.value = option.value;

        // Update price display
        const priceEl = card.querySelector('.cp-card__price--current');
        const compareEl = card.querySelector('.cp-card__price--compare');
        if (priceEl && option.dataset.variantPrice) {
          priceEl.textContent = option.dataset.variantPrice;
        }
        if (compareEl) {
          if (option.dataset.variantCompare) {
            compareEl.textContent = option.dataset.variantCompare;
            compareEl.style.display = '';
            if (priceEl) priceEl.classList.add('cp-card__price--on-sale');
          } else {
            compareEl.style.display = 'none';
            if (priceEl) priceEl.classList.remove('cp-card__price--on-sale');
          }
        }

        // Swap image if variant has one
        if (option.dataset.variantImage) {
          const primaryImg = card.querySelector('.cp-card__img--primary');
          if (primaryImg) {
            primaryImg.src = option.dataset.variantImage;
            primaryImg.removeAttribute('srcset');
          }
        }

        // Update scarcity
        const scarcityEl = card.querySelector('.cp-card__scarcity');
        if (scarcityEl) {
          const qty = parseInt(option.dataset.variantInventory, 10);
          if (qty > 0 && qty <= 5) {
            scarcityEl.innerHTML =
              '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="6"/></svg> Only ' +
              qty + ' left';
            scarcityEl.style.display = 'flex';
          } else {
            scarcityEl.style.display = 'none';
          }
        }

        // Update ATC button state
        const atcBtn = card.querySelector('.js-add-to-cart, .cp-card__atc');
        if (atcBtn && option.dataset.variantAvailable === 'false') {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold Out';
          atcBtn.classList.add('cp-card__atc--sold');
        } else if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to Cart';
          atcBtn.classList.remove('cp-card__atc--sold');
        }
      });
    });
  }

  initCardInteractions();

  /* ============================================
     7. MOBILE STICKY ATC BAR
     ============================================ */
  const stickyBar = document.querySelector('.js-sticky-atc');

  if (stickyBar && window.innerWidth < 768) {
    const stickyImg = stickyBar.querySelector('.js-sticky-img');
    const stickyTitle = stickyBar.querySelector('.js-sticky-title');
    const stickyPrice = stickyBar.querySelector('.js-sticky-price');
    const stickyAddBtn = stickyBar.querySelector('.js-sticky-add');
    let currentStickyCard = null;

    // Show sticky bar when a product card is tapped/focused
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      const card = e.target.closest('.js-product-card');
      if (!card) {
        // Tapped outside a card - hide bar
        if (stickyBar) {
          stickyBar.classList.remove('is-visible');
          currentStickyCard = null;
        }
        return;
      }

      // Don't show for links or button clicks (let them proceed)
      if (e.target.closest('a') || e.target.closest('button') || e.target.closest('form')) {
        return;
      }

      e.preventDefault();
      currentStickyCard = card;
      stickyBar.style.display = 'block';

      const imgEl = card.querySelector('.cp-card__img--primary');
      const titleEl = card.querySelector('.cp-card__title');
      const priceEl = card.querySelector('.cp-card__price--current');
      const variantInput = card.querySelector('.js-card-variant-input');
      const quickBtn = card.querySelector('.js-quick-add');

      if (imgEl) stickyImg.src = imgEl.src;
      if (titleEl) stickyTitle.textContent = titleEl.textContent.trim();
      if (priceEl) stickyPrice.textContent = priceEl.textContent.trim();

      const variantId = variantInput
        ? variantInput.value
        : quickBtn
          ? quickBtn.dataset.variantId
          : '';
      stickyAddBtn.dataset.variantId = variantId;

      requestAnimationFrame(() => {
        stickyBar.classList.add('is-visible');
      });
    });

    // Sticky bar add to cart
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', function () {
        const variantId = this.dataset.variantId;
        if (variantId) {
          addToCart(variantId, this);
        }
      });
    }
  }

  /* ============================================
     8. FAQ SMOOTH ANIMATION
     ============================================ */
  document.querySelectorAll('.cp-faq__item').forEach((details) => {
    const summary = details.querySelector('.cp-faq__question');
    const answer = details.querySelector('.cp-faq__answer');

    if (!summary || !answer) return;

    summary.addEventListener('click', function (e) {
      e.preventDefault();

      if (details.open) {
        // Close
        answer.style.maxHeight = answer.scrollHeight + 'px';
        requestAnimationFrame(() => {
          answer.style.maxHeight = '0';
          answer.style.overflow = 'hidden';
          answer.style.opacity = '0';
        });
        setTimeout(() => {
          details.open = false;
          answer.style.maxHeight = '';
          answer.style.overflow = '';
          answer.style.opacity = '';
        }, 300);
      } else {
        // Open
        details.open = true;
        const height = answer.scrollHeight;
        answer.style.maxHeight = '0';
        answer.style.overflow = 'hidden';
        answer.style.opacity = '0';
        requestAnimationFrame(() => {
          answer.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
          answer.style.maxHeight = height + 'px';
          answer.style.opacity = '1';
        });
        setTimeout(() => {
          answer.style.maxHeight = '';
          answer.style.overflow = '';
          answer.style.transition = '';
        }, 300);
      }
    });
  });

  /* ============================================
     9. SCROLL REVEAL
     ============================================ */
  const revealElements = document.querySelectorAll('[data-scroll-reveal]');
  if (revealElements.length > 0 && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    revealElements.forEach((el) => revealObserver.observe(el));
  }

  /* ============================================
     10. SLOT MODAL
     ============================================ */
  const modal = document.querySelector('.js-cp-modal');
  const modalBody = document.querySelector('.js-cp-modal-body');
  const modalCloseBtn = document.querySelector('.js-cp-modal-close');
  const modalOverlay = document.querySelector('.js-cp-modal-overlay');

  if (modal && modalBody) {
    function openSlotModal(slot) {
      // Clone the slot content into the modal
      const clone = slot.cloneNode(true);
      // Remove the js-cp-slot class so the clone isn't re-clickable
      clone.classList.remove('js-cp-slot');
      clone.removeAttribute('role');
      clone.removeAttribute('tabindex');
      clone.removeAttribute('aria-label');

      // If the clone has a video that was autoplay+muted, restart it
      const videos = clone.querySelectorAll('video');
      videos.forEach(function (video) {
        // Unmute and add controls in the modal for better UX
        video.muted = false;
        video.controls = true;
        video.autoplay = false;
      });

      modalBody.innerHTML = '';
      modalBody.appendChild(clone);
      modal.classList.remove('cp-modal--reviews');
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      cpLockScroll();

      // Pause any autoplay videos in the original slot
      const originalVideos = slot.querySelectorAll('video');
      originalVideos.forEach(function (v) { v.pause(); });
    }

    function closeSlotModal() {
      modal.classList.remove('is-open');
      modal.classList.remove('cp-modal--reviews');
      modal.setAttribute('aria-hidden', 'true');
      cpUnlockScroll();

      // Stop any playing videos in the modal
      const videos = modalBody.querySelectorAll('video');
      videos.forEach(function (v) { v.pause(); });

      // Resume autoplay videos on the page
      document.querySelectorAll('.js-cp-slot video[autoplay]').forEach(function (v) {
        v.play().catch(function () {});
      });

      setTimeout(function () {
        modalBody.innerHTML = '';
      }, 300);
    }

    // Click handler for slots
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      const slot = e.target.closest('.js-cp-slot');
      if (!slot) return;

      // Don't intercept clicks on links or buttons within the slot
      if (e.target.closest('a') || e.target.closest('button')) return;

      e.preventDefault();
      openSlotModal(slot);
    });

    // Keyboard handler for slots (Enter/Space)
    document.addEventListener('keydown', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.key === 'Enter' || e.key === ' ') {
        const slot = e.target.closest('.js-cp-slot');
        if (slot && e.target === slot) {
          e.preventDefault();
          openSlotModal(slot);
        }
      }
    });

    // Notes button — opens modal with scent notes
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      const notesBtn = e.target.closest('.js-notes-btn');
      if (!notesBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const notes = notesBtn.dataset.notes;
      const productTitle = notesBtn.dataset.productTitle || '';
      if (!notes) return;

      // Notes can be pipe-delimited (from tags) or a simple string (from metafield)
      const noteArray = notes.indexOf('|') > -1 ? notes.split('|') : notes.split(',');
      let html = '<div class="cp-modal__notes">';
      html += '<h3 class="cp-modal__notes-title">' + productTitle + '</h3>';
      html += '<p class="cp-modal__notes-subtitle">Scent Notes</p>';
      html += '<div class="cp-modal__notes-list">';
      noteArray.forEach(function (note) {
        const trimmed = note.trim();
        if (!trimmed) return;
        const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
        html += '<span class="cp-modal__note-tag">' + capitalized + '</span>';
      });
      html += '</div></div>';

      modalBody.innerHTML = html;
      modal.classList.remove('cp-modal--reviews');
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      cpLockScroll();
    });

    /* ------------------------------------------------
       Reviews modal — opened from a card's star rating
       ------------------------------------------------
       Same Apps Script feed the cart drawer reads. Fetched once per page and
       cached, then filtered to the clicked product's handle so a product with
       no reviews never shows someone else's. */
    var REVIEWS_API = 'https://script.google.com/macros/s/AKfycbxU_lJJ6jhDuOSL5VmAwW1bCKT7cV-KHMH1mQ3eWKV3f8BY3aFZYry9o22q0dJHACTf/exec';
    var reviewsPromise = null;

    function loadAllReviews() {
      if (!reviewsPromise) {
        reviewsPromise = fetch(REVIEWS_API + '?action=get_reviews')
          .then(function (r) { return r.json(); })
          .then(function (d) { return (d && d.reviews) || []; })
          .catch(function () { return null; }); // null = failed, [] = genuinely empty
      }
      return reviewsPromise;
    }

    function esc(s) {
      var d = document.createElement('div');
      d.textContent = s == null ? '' : String(s);
      return d.innerHTML;
    }

    function starsHtml(rating) {
      var r = parseFloat(rating) || 0;
      return '<span class="cp-modal__review-stars" aria-label="' + r + ' out of 5">' +
             '<span class="cp-stars" style="--rating:' + r + ';">' +
             '<span class="cp-stars__filled">★★★★★</span>' +
             '<span class="cp-stars__empty">☆☆☆☆☆</span></span></span>';
    }

    function openReviewsModal(trigger) {
      var handle = trigger.dataset.productHandle || '';
      var title = trigger.dataset.productTitle || '';
      var rating = trigger.dataset.rating || '';
      var count = trigger.dataset.reviewCount || '';

      // Shell + skeleton first so the modal opens instantly
      modalBody.innerHTML =
        '<div class="cp-modal__reviews">' +
          '<div class="cp-modal__reviews-head">' +
            '<h3 class="cp-modal__reviews-title">' + esc(title) + '</h3>' +
            '<p class="cp-modal__reviews-summary" data-reviews-summary>' + starsHtml(rating) +
              (count ? '<span>' + esc(count) + ' review' + (count === '1' ? '' : 's') + '</span>' : '') +
            '</p>' +
          '</div>' +
          '<div class="cp-modal__reviews-list" data-reviews-list>' +
            '<div class="cp-modal__review cp-modal__review--skel"><span class="cp-rskel cp-rskel--name"></span><span class="cp-rskel cp-rskel--line"></span><span class="cp-rskel cp-rskel--line cp-rskel--short"></span></div>'.repeat(3) +
          '</div>' +
        '</div>';
      modal.classList.add('is-open');
      modal.classList.add('cp-modal--reviews'); // shell stops scrolling; list owns it
      modal.setAttribute('aria-hidden', 'false');
      cpLockScroll();

      loadAllReviews().then(function (all) {
        var list = modalBody.querySelector('[data-reviews-list]');
        if (!list) return; // modal closed while loading

        if (all === null) {
          list.innerHTML = '<p class="cp-modal__reviews-empty">Couldn\'t load reviews right now. ' +
            '<a href="' + esc(trigger.getAttribute('href')) + '">See them on the product page</a>.</p>';
          return;
        }

        var mine = all.filter(function (r) { return (r.product_handle || '') === handle; });
        // newest first when a real timestamp exists; rows with only relative
        // dates ("4 months ago") keep their sheet order
        mine.sort(function (a, b) {
          var ta = Date.parse(a.created_at || '') || 0;
          var tb = Date.parse(b.created_at || '') || 0;
          return tb - ta;
        });

        if (!mine.length) {
          list.innerHTML = '<p class="cp-modal__reviews-empty">No written reviews for this one yet.</p>';
          return;
        }

        // The star rating and count in the header came from Shopify's
        // reviews.rating_count metafield, but the LIST comes from the sheet —
        // two different sources that disagree (Wine Down: metafield 2, sheet 7).
        // Re-derive both from the rows actually being shown so the header can
        // never contradict the list beneath it.
        var head = modalBody.querySelector('[data-reviews-summary]');
        if (head) {
          var sum = 0, rated = 0;
          mine.forEach(function (r) {
            var v = parseFloat(r.rating);
            if (v > 0) { sum += v; rated++; }
          });
          var avg = rated ? Math.round((sum / rated) * 10) / 10 : parseFloat(rating) || 0;
          head.innerHTML = starsHtml(avg) +
            '<span>' + mine.length + ' review' + (mine.length === 1 ? '' : 's') + '</span>';
        }

        list.innerHTML = mine.map(function (r) {
          var body = r.text || r.review || r.body || '';
          var when = r.date || '';
          // Customer photos. The feed exposes them as `photos` (with `media_urls`
          // as an alias); only a handful of rows carry any, so this stays absent
          // rather than empty for most reviews.
          var shots = r.photos || r.media_urls || [];
          if (!Array.isArray(shots)) shots = shots ? [shots] : [];
          var shotsHtml = '';
          if (shots.length) {
            shotsHtml = '<div class="cp-modal__review-photos">' +
              shots.map(function (src) {
                return '<a class="cp-modal__review-photo" href="' + esc(src) + '" target="_blank" rel="noopener">' +
                       '<img src="' + esc(src) + '" alt="Customer photo from ' + esc(r.name || 'a review') + '" ' +
                       'loading="lazy" decoding="async" width="88" height="88"></a>';
              }).join('') + '</div>';
          }
          return '<div class="cp-modal__review">' +
                   '<div class="cp-modal__review-top">' +
                     '<span class="cp-modal__review-name">' + esc(r.name || 'Verified buyer') + '</span>' +
                     starsHtml(r.rating) +
                   '</div>' +
                   (r.title ? '<p class="cp-modal__review-title">' + esc(r.title) + '</p>' : '') +
                   '<p class="cp-modal__review-text">' + esc(body) + '</p>' +
                   shotsHtml +
                   (when ? '<span class="cp-modal__review-date">' + esc(when) + '</span>' : '') +
                 '</div>';
        }).join('');
      });
    }

    /* ------------------------------------------------
       Reconcile the card star counts with the review feed
       ------------------------------------------------
       The number on each card comes from Shopify's reviews.rating_count
       metafield, which has not kept up with the Etsy/Shop reviews landing in
       the sheet — every product was undercounted (Room Service showed 9 against
       14, cards totalled 32 against 70). Rather than let the card contradict
       the modal that opens from it, both now read the same source.

       Deferred to after load + idle: the feed is a single ~33KB JSON already
       fetched on click, and this must never compete with LCP. Only the existing
       count/rating are rewritten — no rows are inserted — so this cannot shift
       layout. Fixing the metafields at the source makes this a no-op. */
    function reconcileCardCounts() {
      var cards = document.querySelectorAll('.js-card-reviews');
      if (!cards.length) return;
      loadAllReviews().then(function (all) {
        if (!all || !all.length) return;
        var byHandle = {};
        all.forEach(function (r) {
          var h = r.product_handle || '';
          if (!h) return;
          if (!byHandle[h]) byHandle[h] = { n: 0, sum: 0, rated: 0 };
          byHandle[h].n++;
          var v = parseFloat(r.rating);
          if (v > 0) { byHandle[h].sum += v; byHandle[h].rated++; }
        });
        cards.forEach(function (a) {
          var agg = byHandle[a.dataset.productHandle || ''];
          if (!agg || !agg.n) return;
          var avg = agg.rated ? Math.round((agg.sum / agg.rated) * 10) / 10 : parseFloat(a.dataset.rating) || 0;
          a.dataset.reviewCount = agg.n;
          a.dataset.rating = avg;
          var stars = a.querySelector('.cp-stars');
          if (stars) stars.style.setProperty('--rating', avg);
          var countEl = a.querySelector('.cp-card__review-count');
          /* Older markup omitted the count span when the metafield count was
             0, which left nothing to write the feed's number into. */
          if (!countEl) {
            countEl = document.createElement('span');
            countEl.className = 'cp-card__review-count';
            a.appendChild(countEl);
          }
          countEl.textContent = '(' + agg.n + ')';
          /* Products with no Shopify rating metafield render the anchor hidden
             but space-reserved (.cp-card__rating--empty). The feed has reviews
             for this one, so reveal it — no layout shift, the row was already
             occupying its height. */
          a.classList.remove('cp-card__rating--empty');
          a.setAttribute('aria-label',
            'Rated ' + avg + ' out of 5 stars from ' + agg.n +
            (agg.n === 1 ? ' review' : ' reviews') + ' — read reviews');
        });
      });
    }
    cpReconcile = reconcileCardCounts;

    function whenIdle(fn) {
      if ('requestIdleCallback' in window) { requestIdleCallback(fn, { timeout: 3000 }); }
      else { setTimeout(fn, 800); }
    }
    if (document.readyState === 'complete') { whenIdle(reconcileCardCounts); }
    else { window.addEventListener('load', function () { whenIdle(reconcileCardCounts); }); }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var trigger = e.target.closest('.js-card-reviews');
      if (!trigger) return;
      e.preventDefault();   // stay on the collection page
      e.stopPropagation();
      openReviewsModal(trigger);
    });

    // Close modal
    if (modalCloseBtn) {
      modalCloseBtn.addEventListener('click', closeSlotModal);
    }
    if (modalOverlay) {
      modalOverlay.addEventListener('click', closeSlotModal);
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        closeSlotModal();
      }
    });
  }
})();
